import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { X, AlertCircle, EyeOffIcon } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { SettingsIcon } from '@/components/icons/settings-icon';
import { BoltIcon } from '@/components/icons/bolt-icon';
import { Tooltip } from '@nextui-org/react';
import { Switch } from '@nextui-org/react';
import { cn } from '@/lib/utils';
import { logoPlaceholder } from '@/utils/placeholders';
import { useBreakpoint } from '@/lib/hooks/use-breakpoint';
import { aspectRatios } from '@/components/ui/image-size';
import { LoraModel } from '@/types/lora';
import { TriggerWordsGuide } from '../generate-sub/trigger-words-guide';

interface PromptSectionProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  handleGenerate: () => void;
  isGenerating: boolean;
  currentRatio: (typeof aspectRatios)[0];
  selectedLoraModel: LoraModel | null;
  setSelectedLoraModel: (model: LoraModel | null) => void;
  missingTriggerWords: string[];
  setIsAdvancedModalOpen: (isOpen: boolean) => void;
  handleResetSettings: () => void;
  validatePrompt: (prompt: string) => boolean;
  requiredTriggers: string[];
  stealthMode: boolean;
  setStealthMode: (value: boolean) => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
}

export const PromptSection: React.FC<PromptSectionProps> = ({
  prompt,
  setPrompt,
  handleGenerate,
  isGenerating,
  currentRatio,
  selectedLoraModel,
  setSelectedLoraModel,
  missingTriggerWords,
  setIsAdvancedModalOpen,
  handleResetSettings,
  validatePrompt,
  requiredTriggers,
  stealthMode,
  setStealthMode,
  textareaRef,
}) => {
  const [showTriggerAlert, setShowTriggerAlert] = useState(false);
  const [showButtonAlert, setShowButtonAlert] = useState(false);
  const [localPrompt, setLocalPrompt] = useState(prompt);
  const breakpoint = useBreakpoint();

  const debouncedValidate = useDebounce((value: string) => {
    setPrompt(value);
    validatePrompt(value);
  }, 100);

  useEffect(() => {
    setLocalPrompt(prompt);
  }, [prompt]);

  const handleGenerateClick = () => {
    if (missingTriggerWords.length > 0) {
      setShowTriggerAlert(true);
      setShowButtonAlert(true);
      setTimeout(() => {
        setShowTriggerAlert(false);
        setShowButtonAlert(false);
      }, 2000);
      return;
    }
    handleGenerate();
  };

  return (
    <div className="mb-4 flex flex-col space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        <div className="flex flex-1 flex-col gap-4">
          <textarea
            ref={textareaRef}
            className="h-32 w-full resize-none rounded-md border border-gray-300 bg-gray-50 p-3 duration-200 transition-colors focus:border-brand focus:ring-brand dark:border-gray-700 dark:bg-gray-800"
            placeholder="Enter a prompt"
            value={localPrompt}
            onChange={(e) => {
              setLocalPrompt(e.target.value);
              debouncedValidate(e.target.value);
            }}
            onFocus={() => {
              if (breakpoint === 'xs' || breakpoint === 'sm') {
                setTimeout(() => {
                  textareaRef?.current?.scrollIntoView({
                    behavior: 'instant',
                    block: 'start',
                  });
                  window.scrollBy(0, -100);
                }, 100);
              }
            }}
          />

          <div className="relative">
            <button
              className={`group relative h-12 w-full rounded-lg px-4 py-2 ${
                isGenerating
                  ? 'cursor-not-allowed bg-gray-400'
                  : 'bg-brand hover:bg-brand-dark'
              } flex items-center gap-2 text-white duration-300 transition-all`}
              onClick={handleGenerateClick}
              disabled={isGenerating}
            >
              <span className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 opacity-0 blur duration-300 transition-opacity group-hover:animate-shine-shadow group-hover:opacity-100" />

              <div className="pointer-events-none relative z-10 flex w-full items-center justify-center gap-2">
                <BoltIcon className="h-5 w-5" />
                <span className="font-medium">
                  {isGenerating ? 'Generating...' : 'Generate Image'}
                </span>
              </div>
            </button>

            <div
              className={`absolute -top-12 left-1/2 flex -translate-x-1/2 
              transform items-center gap-2 whitespace-nowrap rounded-lg bg-red-500
              px-4 py-2 text-sm text-white shadow-lg
              duration-300 transition-all 
              ${
                showButtonAlert
                  ? 'translate-y-0 opacity-100'
                  : 'pointer-events-none translate-y-2 opacity-0'
              }`}
            >
              <AlertCircle className="h-4 w-4" />
              <span>Missing required trigger words!</span>
              <div
                className="absolute -bottom-2 left-1/2 -translate-x-1/2 transform 
                border-l-8 border-r-8 border-t-8 border-transparent 
                border-t-red-500"
              />
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-2 rounded-md border border-gray-200 p-2 dark:border-gray-700 xl:min-w-[180px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-600 dark:text-gray-300">
              {currentRatio?.name}
            </span>
            {selectedLoraModel && (
              <>
                <span className="text-xs text-gray-400">•</span>
                <div className="flex items-center gap-1">
                  <Image
                    src={selectedLoraModel?.image ?? logoPlaceholder}
                    alt={selectedLoraModel.name}
                    width={12}
                    height={12}
                    className="rounded-full"
                  />
                  <span className="max-w-[100px] truncate text-xs text-gray-600 dark:text-gray-300">
                    {selectedLoraModel.name}
                  </span>
                  <button
                    onClick={() => setSelectedLoraModel(null)}
                    className="ml-0.5 text-gray-400 duration-200 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 pt-2 dark:border-gray-700">
            <button
              onClick={handleResetSettings}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Reset
            </button>
            <button
              onClick={() => setIsAdvancedModalOpen(true)}
              className="rounded-md bg-gray-100 p-1.5 duration-200 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
            >
              <SettingsIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 pt-2 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <EyeOffIcon
                className={cn(
                  'h-3.5 w-3.5 duration-200 transition-colors',
                  stealthMode ? 'text-brand' : 'text-gray-500'
                )}
              />
              <span
                className={cn(
                  'text-xs duration-200 transition-colors',
                  stealthMode
                    ? 'font-medium text-brand'
                    : 'text-gray-600 dark:text-gray-300'
                )}
              >
                Stealth Mode
              </span>
            </div>
            <Tooltip
              content={
                stealthMode
                  ? 'Images will be private by default'
                  : 'Image will be public by default'
              }
            >
              <div>
                <Switch
                  size="sm"
                  color={stealthMode ? 'primary' : 'default'}
                  checked={stealthMode}
                  isSelected={stealthMode}
                  onChange={(e) => setStealthMode(e.target.checked)}
                />
              </div>
            </Tooltip>
          </div>
        </div>
      </div>

      <div
        className={`transform duration-300 transition-all
        ${
          showTriggerAlert ? 'scale-105 animate-bounce-horizontal' : 'scale-100'
        }`}
      >
        <TriggerWordsGuide
          requiredTriggers={requiredTriggers}
          missingTriggerWords={missingTriggerWords}
          handleTriggerClick={(trigger) => {
            if (!prompt.toLowerCase().includes(trigger.toLowerCase())) {
              const newPrompt =
                prompt.trim() + (prompt.trim() ? ' ' : '') + trigger;
              setPrompt(newPrompt);
              validatePrompt(newPrompt);
            }
          }}
        />
      </div>
    </div>
  );
};
